/**
 * Enterprise Delivery Intelligence™ — module catalogue
 * Organised around the SGS Bateman business lifecycle:
 *   Business Development · Engineering · Procurement · Commercial · Knowledge · Executive
 */

export const EDI_SECTIONS = [
    { key: "Business Development", accent: "#D4AF37" },
    { key: "Engineering",          accent: "#3B82F6" },
    { key: "Procurement",          accent: "#F59E0B" },
    { key: "Commercial",           accent: "#10B981" },
    { key: "Knowledge",            accent: "#94A3B8" },
    { key: "Executive",            accent: "#EAB308" },
];

export const EDI_MODULES = [
    // =========================================================================
    // BUSINESS DEVELOPMENT
    // =========================================================================
    {
        key: "bid_intelligence",
        name: "Bid Intelligence™",
        section: "Business Development",
        icon: "Target",
        tagline: "Prepare bids faster with historical intelligence and similar-project matching.",
        purpose: "Similar project search, historical tender and estimate search, win-probability analysis, historical bid performance and proposal intelligence — all in one workspace to accelerate bid preparation and improve strike rates.",
        kpis: [
            { label: "Win Rate", value: "48%" },
            { label: "Avg Bid Prep", value: "11.4 d" },
            { label: "Historical Bids", value: "412" },
            { label: "Proposal Templates", value: "84" },
        ],
        table: {
            title: "Live Bid Radar",
            columns: ["Opportunity", "Client", "Sector", "Similar Bid", "Win Prob.", "Status"],
            rows: [
                ["SGSB-OPP-2026-042", "Sibanye-Stillwater",   "Gold",         "Harmony Kusasalethu (94%)", "72%", "Proposal"],
                ["SGSB-OPP-2026-043", "Anglo American",       "PGM",          "Rustenburg (88%)",          "64%", "Qualification"],
                ["SGSB-OPP-2026-044", "First Quantum",        "Copper",       "Okavango (82%)",            "58%", "Screening"],
                ["SGSB-OPP-2026-045", "Vale Moçambique",      "Coal",         "Tete (91%)",                "78%", "Proposal"],
                ["SGSB-OPP-2026-046", "OCP Africa",           "Chemicals",    "Nampula (86%)",             "62%", "Proposal"],
                ["SGSB-OPP-2026-047", "Hive Hydrogen",        "Energy",       "Saldanha (79%)",            "54%", "Screening"],
            ],
        },
        aiPrompts: [
            "Which historical bids are most similar to a new 4Mtpa gold plant tender?",
            "Estimate win probability for SGSB-OPP-2026-042 and explain the drivers.",
        ],
    },
    {
        key: "engineering_estimate",
        name: "Engineering Estimate Intelligence™",
        section: "Business Development",
        icon: "Calculator",
        tagline: "Estimate engineering hours, cost and risk from historical delivery.",
        purpose: "Automatically recommends historical engineering hours, costs, equipment, suppliers, typical risks, historical productivity, expected schedule, estimate confidence and knowledge reuse opportunities for any new project.",
        kpis: [
            { label: "Estimate Accuracy", value: "92.4%" },
            { label: "Median Variance", value: "±6.1%" },
            { label: "Baselines Indexed", value: "218" },
            { label: "Confidence", value: "High" },
        ],
        table: {
            title: "Historical Estimate Baselines",
            columns: ["Baseline", "Type", "Scope", "Hours (k)", "Actual Δ", "Confidence"],
            rows: [
                ["Harmony Kusasalethu",   "Gold CIP",       "4.0 Mtpa",  "184",  "+3.2%",  "High"],
                ["Great Basin Burnstone", "Gold CIL",       "3.6 Mtpa",  "168",  "+5.8%",  "High"],
                ["Sibanye Beatrix",       "Concentrator",   "6.2 Mtpa",  "142",  "-2.1%",  "High"],
                ["Kolomela Concentrator", "Iron Ore",       "8.4 Mtpa",  "218",  "+4.6%",  "High"],
                ["Rustenburg Smelter",    "PGM Smelter",    "Modernise", "162",  "+7.4%",  "Medium"],
                ["Okavango Copper",       "Copper Leach",   "3.2 Mtpa",  "138",  "+5.1%",  "High"],
                ["Nampula Ammonia",       "Ammonia FEED",   "1.4 Mtpa",  "82",   "+11.2%", "Medium"],
            ],
        },
        aiPrompts: [
            "Estimate engineering hours for a 4Mtpa gold plant using the best historical baseline.",
            "Which projects are the strongest productivity benchmarks for a copper leach expansion?",
        ],
    },

    // =========================================================================
    // ENGINEERING
    // =========================================================================
    {
        key: "eng_knowledge_repo",
        name: "Engineering Knowledge Repository™",
        section: "Engineering",
        icon: "FolderTree",
        tagline: "Discipline-organised view of the corporate knowledge base.",
        purpose: "The engineering-facing view of the Enterprise Knowledge Repository — organised by discipline (Mechanical, Piping, E&I, Civil, Structural, Process, Instrumentation, HVAC, Fire, Environmental).",
        kpis: [
            { label: "Disciplines", value: "12" },
            { label: "Docs Indexed", value: "1.42M" },
            { label: "Avg Retrieval", value: "6.2 s" },
            { label: "Reuse Rate", value: "47.3%" },
        ],
        table: {
            title: "By Discipline",
            columns: ["Discipline", "Documents", "Data Sheets", "Drawings", "Reuse %"],
            rows: [
                ["Mechanical",       "218,400", "6,120", "42,180", "62%"],
                ["Piping",           "184,200", "1,840", "38,610", "48%"],
                ["Electrical",       "148,600", "2,410", "24,180", "42%"],
                ["Instrumentation",  "112,400", "3,180", "18,940", "51%"],
                ["Civil",            "94,800",  "1,240", "14,610", "38%"],
                ["Structural",       "88,200",  "820",   "22,410", "44%"],
                ["Process",          "142,600", "2,180", "16,240", "58%"],
                ["HVAC",             "24,180",  "412",   "6,120",  "31%"],
            ],
        },
        aiPrompts: [
            "Which mechanical data sheets are most re-used across the portfolio?",
            "Find similar piping specifications used in Nampula Ammonia.",
        ],
    },
    {
        key: "reuse",
        name: "Engineering Knowledge Reuse™",
        section: "Engineering",
        icon: "Recycle",
        tagline: "Reduce engineering effort through intelligent reuse.",
        purpose: "When an engineer starts a new project, the platform analyses the repository, matches similar historical projects, and recommends reusable content for immediate baseline creation.",
        kpis: [
            { label: "Reuse Rate", value: "47.3%" },
            { label: "Similarity Threshold", value: "≥ 78%" },
            { label: "Hours Saved (YTD)", value: "218,400" },
            { label: "Baseline Time Saved", value: "68%" },
        ],
        table: {
            title: "Live Match — New Gold Plant Project (Client: Sibanye-Stillwater)",
            columns: ["Historical Project", "Type", "Similarity", "Reusable Assets", "Est. Hrs Saved"],
            rows: [
                ["Harmony Kusasalethu Gold CIP", "Gold CIP Plant",     "94%", "Data sheets, PFDs, P&IDs, Struct. calcs, Mech specs", "2,180"],
                ["Great Basin Gold Burnstone",   "Gold CIL Plant",     "88%", "P&IDs, Equipment schedules, Vendor packages",         "1,640"],
                ["Sibanye Beatrix",              "Gold Concentrator",  "82%", "BOQs, Instrument lists",                              "1,120"],
                ["AngloGold Mponeng",            "Gold Refining",      "79%", "Data sheets, Standard specs",                         "820"],
                ["Pan African Barberton",        "Gold Beneficiation", "76%", "Mechanical specs, Procurement packages",              "640"],
            ],
        },
        aiPrompts: [
            "Recommend the top 5 reusable assets for the new Sibanye Gold Plant.",
            "Compare this new gold plant scope against historical CIP builds.",
        ],
    },
    {
        key: "templates",
        name: "Intelligent Design Templates™",
        section: "Engineering",
        icon: "LayoutTemplate",
        tagline: "Generate engineering deliverables from historical projects.",
        purpose: "Auto-prepare equipment data sheets, mechanical, civil, electrical, instrument and procurement specifications, and inspection templates. Highlight only fields requiring engineer review.",
        kpis: [
            { label: "Template Library", value: "612" },
            { label: "Fields Auto-Filled", value: "83%" },
            { label: "Engineer Review Fields", value: "17%" },
            { label: "Cycle Time", value: "-72%" },
        ],
        table: {
            title: "Live Templates",
            columns: ["Template", "Discipline", "Historical Basis", "Auto Fields", "Review Fields"],
            rows: [
                ["Equipment Data Sheet — Rip Tank",   "Mechanical",     "18 projects", "184", "38"],
                ["Mechanical Spec — Pumps",           "Mechanical",     "42 projects", "212", "24"],
                ["Civil Spec — Foundations",          "Civil",          "26 projects", "148", "42"],
                ["Electrical Spec — MV Switchgear",   "Electrical",     "22 projects", "168", "18"],
                ["Instrument Spec — Flow Meters",     "Instrumentation","31 projects", "112", "12"],
                ["Procurement Spec — Structural Steel","Procurement",   "38 projects", "94",  "8"],
                ["Inspection Template — Weld QA",     "Quality",        "44 projects", "62",  "6"],
            ],
        },
        aiPrompts: [
            "Generate an equipment data sheet template for a 4.2m diameter thickener.",
            "Which templates require refresh based on recent code updates?",
        ],
    },
    {
        key: "datasheet",
        name: "Automatic Data Sheet Generation™",
        section: "Engineering",
        icon: "FileSpreadsheet",
        tagline: "Auto-generate draft equipment data sheets using historical engineering knowledge.",
        purpose: "Automatically populate dimensions, materials, power, operating conditions and vendor history for common equipment classes. Engineer approves the final version.",
        kpis: [
            { label: "Equipment Classes", value: "10" },
            { label: "Avg Draft Time", value: "1.8 h" },
            { label: "Baseline Time", value: "12 h" },
            { label: "Auto-Populated Fields", value: "83%" },
        ],
        table: {
            title: "Supported Equipment Classes",
            columns: ["Equipment", "Historical Refs", "Vendor History", "Materials", "Approval SLA"],
            rows: [
                ["Rip Tanks",         "38",  "12 vendors", "SS304 / SS316L / HDPE lined",   "48 h"],
                ["Thickeners",        "44",  "9 vendors",  "Carbon steel, epoxy lined",     "48 h"],
                ["Cyclones",          "56",  "6 vendors",  "Ceramic lined / rubber lined",  "24 h"],
                ["Pumps",             "128", "18 vendors", "Various — slurry, process",     "24 h"],
                ["Screens",           "62",  "8 vendors",  "SS + polyurethane deck",        "48 h"],
                ["Conveyors",         "94",  "12 vendors", "Steel structure, rubber belt",  "72 h"],
                ["Crushers",          "38",  "6 vendors",  "Manganese steel liners",        "72 h"],
                ["Reactors",          "22",  "5 vendors",  "SS316L / duplex / hastelloy",   "96 h"],
                ["Columns",           "18",  "4 vendors",  "SS304 / titanium clad",         "96 h"],
                ["Pressure Vessels",  "84",  "9 vendors",  "Carbon steel per ASME VIII",    "72 h"],
            ],
        },
        aiPrompts: [
            "Generate a rip tank data sheet for a 4Mtpa Gold CIP plant.",
            "Which historical pump models are best for a slurry duty at 42 m³/h and 65m TDH?",
        ],
    },
    {
        key: "drawing_approval",
        name: "Drawing Approval Intelligence™",
        section: "Engineering",
        icon: "Stamp",
        tagline: "Track engineering approvals end-to-end.",
        purpose: "Monitor issued, under-review, approved, rejected and overdue drawings with average approval duration, reviewer workload and revision history.",
        kpis: [
            { label: "Under Review", value: "418" },
            { label: "Avg Approval", value: "3.4 days" },
            { label: "Overdue", value: "38" },
            { label: "First-Pass Rate", value: "84%" },
        ],
        table: {
            title: "Approvals Queue (Top 10)",
            columns: ["Drawing", "Project", "Discipline", "Reviewer", "Age (d)", "Status"],
            rows: [
                ["SGSB-1001-DWG-0184", "Kolomela Concentrator",  "Mechanical",     "K. Molefe",  "1", "Under Review"],
                ["SGSB-1002-DWG-0092", "Rustenburg Smelter",     "Electrical",     "A. Steyn",   "2", "Under Review"],
                ["SGSB-1003-DWG-0021", "Nampula Ammonia",        "Piping",         "P. Naidoo",  "5", "Overdue"],
                ["SGSB-1004-DWG-0244", "Tete Beneficiation",     "Instrumentation","T. Adeyemi", "1", "Under Review"],
                ["SGSB-1005-DWG-0102", "Okavango Copper",        "Civil",          "K. Molefe",  "3", "Under Review"],
                ["SGSB-1006-DWG-0011", "Cape Town Desalination", "Process",        "A. Steyn",   "6", "Overdue"],
                ["SGSB-1007-DWG-0068", "Saldanha Hydrogen",      "Mechanical",     "P. Naidoo",  "2", "Under Review"],
                ["SGSB-1008-DWG-0402", "Lubumbashi Refinery",    "Electrical",     "T. Adeyemi", "0", "Issued"],
                ["SGSB-1001-DWG-0185", "Kolomela Concentrator",  "Structural",     "K. Molefe",  "1", "Under Review"],
                ["SGSB-1005-DWG-0103", "Okavango Copper",        "Piping",         "P. Naidoo",  "4", "Under Review"],
            ],
        },
        aiPrompts: [
            "Which reviewers are overloaded this week?",
            "Predict which drawings will slip approval this cycle.",
        ],
    },
    {
        key: "lessons_learned",
        name: "Lessons Learned™",
        section: "Engineering",
        icon: "BookOpen",
        tagline: "Capture lessons once — apply them across every future project.",
        purpose: "Structured lessons learned repository with automatic surfacing on similar projects, categorised by discipline, phase and impact.",
        kpis: [
            { label: "Lessons Captured", value: "1,842" },
            { label: "Projects Contributing", value: "218" },
            { label: "Applied This Year", value: "412" },
            { label: "Categories", value: "24" },
        ],
        table: {
            title: "Recent High-Impact Lessons",
            columns: ["Ref", "Project", "Discipline", "Category", "Impact", "Applied"],
            rows: [
                ["SGSB-LL-000188", "Lubumbashi Refinery",   "Electrical", "Commissioning",    "High",   "12 times"],
                ["SGSB-LL-000191", "Tete Beneficiation",    "Piping",     "Slurry Erosion",   "High",   "18 times"],
                ["SGSB-LL-000195", "Kolomela Concentrator", "Structural", "Foundation Design","Medium", "9 times"],
                ["SGSB-LL-000201", "Rustenburg Smelter",    "Process",    "Refractory Life",  "High",   "6 times"],
                ["SGSB-LL-000214", "Nampula Ammonia",       "Process",    "FEED Gating",      "High",   "4 times"],
                ["SGSB-LL-000218", "Okavango Copper",       "Mechanical", "SX Contactor",     "Medium", "8 times"],
            ],
        },
        aiPrompts: [
            "Show lessons learned relevant to a new gold CIP plant design.",
            "Which lessons should be applied to Nampula Ammonia this cycle?",
        ],
    },
    {
        key: "standard_library",
        name: "Standard Engineering Library™",
        section: "Engineering",
        icon: "BookMarked",
        tagline: "Curated corporate standards for reuse across every project.",
        purpose: "Standardised specifications, drawings, procedures, calculations and templates — the go-to source of engineering standards, versioned and quarterly-refreshed.",
        kpis: [
            { label: "Standards Published", value: "612" },
            { label: "Auto-Adopted", value: "82%" },
            { label: "Revision Cadence", value: "Quarterly" },
            { label: "Compliance", value: "98%" },
        ],
        table: {
            title: "Standards Catalogue",
            columns: ["Standard", "Discipline", "Version", "Adopted (Projects)", "Owner"],
            rows: [
                ["STD-MECH-018 · Slurry Pumps",              "Mechanical",     "v3.2", "42", "K. Molefe"],
                ["STD-PIPE-024 · Slurry Pipeline Design",    "Piping",         "v2.6", "38", "P. Naidoo"],
                ["STD-ELEC-011 · MV Switchgear",             "Electrical",     "v4.1", "34", "A. Steyn"],
                ["STD-INST-008 · Flow Meter Selection",      "Instrumentation","v2.3", "28", "T. Adeyemi"],
                ["STD-CIVIL-006 · Foundation Design",        "Civil",          "v3.0", "48", "K. Molefe"],
                ["STD-STRUC-004 · Steel Structure",          "Structural",     "v2.8", "44", "K. Molefe"],
                ["STD-PROC-014 · SX Contactor Design",       "Process",        "v1.4", "12", "P. Naidoo"],
                ["STD-QA-002 · Weld Inspection Acceptance",  "Quality",        "v5.0", "62", "A. Steyn"],
            ],
        },
        aiPrompts: [
            "Which standards should be used for a new 4Mtpa gold plant?",
            "Are any standards due for revision this quarter?",
        ],
    },

    // =========================================================================
    // PROCUREMENT
    // =========================================================================
    {
        key: "procurement_intel",
        name: "Procurement Intelligence™",
        section: "Procurement",
        icon: "Truck",
        tagline: "Transform Procurement Operating Plans into live intelligence.",
        purpose: "Monitor RFQs, bid evaluations, purchase orders, manufacturing, inspection, factory acceptance, shipping, customs, delivery and commissioning readiness with AI warnings and supplier performance.",
        kpis: [
            { label: "Active POs", value: "138" },
            { label: "Long-Lead Items", value: "24" },
            { label: "Delivery OTIF", value: "88.4%" },
            { label: "Supplier Score", value: "4.2 / 5" },
        ],
        table: {
            title: "Long-Lead Item Watchlist",
            columns: ["Item", "Project", "Supplier", "Status", "ETA", "Risk"],
            rows: [
                ["SAG Mill Shell",           "Kolomela Concentrator",  "Metso Outotec",   "Manufacturing", "12 wks", "Medium"],
                ["Ammonia Reactor Package",  "Nampula Ammonia",        "Casale",          "FAT",           "6 wks",  "High"],
                ["MV Switchgear",            "Rustenburg Smelter",     "ABB",             "Shipping",      "3 wks",  "Low"],
                ["Slurry Pump Set",          "Okavango Copper",        "Weir Minerals",   "Manufacturing", "8 wks",  "Medium"],
                ["Desalination Membranes",   "Cape Town Desal",        "DuPont",          "RFQ",           "TBD",    "High"],
                ["Hydrogen Compressor Pkg",  "Saldanha Hydrogen",      "Atlas Copco",     "Manufacturing", "18 wks", "Medium"],
                ["Cobalt Refinery Cranes",   "Lubumbashi Refinery",    "Konecranes",      "Delivery",      "1 wk",   "Low"],
            ],
        },
        aiPrompts: [
            "Which long-lead items threaten commissioning readiness?",
            "Recommend a supplier diversification strategy for reactor packages.",
        ],
    },
    {
        key: "pop",
        name: "Procurement Operating Plan (POP)",
        section: "Procurement",
        icon: "ClipboardList",
        tagline: "Interactive procurement dashboard — replaces static documents.",
        purpose: "Package status, procurement schedule, long-lead items, critical path procurement, delayed deliveries, supplier performance and executive actions in one operational surface.",
        kpis: [
            { label: "Packages Live", value: "62" },
            { label: "On Schedule", value: "48" },
            { label: "At Risk", value: "10" },
            { label: "Delayed", value: "4" },
        ],
        table: {
            title: "Package Status",
            columns: ["Package", "Project", "Value", "Milestone", "Schedule", "Status"],
            rows: [
                ["SGSB-1001-PKG-01", "Kolomela Concentrator", "$18.4M", "PO Placed",       "On track", "Green"],
                ["SGSB-1002-PKG-02", "Rustenburg Smelter",    "$12.1M", "Bid Evaluation",  "-2 wks",   "Amber"],
                ["SGSB-1003-PKG-01", "Nampula Ammonia",       "$41.2M", "RFQ",             "-5 wks",   "Red"],
                ["SGSB-1004-PKG-03", "Tete Beneficiation",    "$8.9M",  "Delivery",        "+1 wk",    "Green"],
                ["SGSB-1005-PKG-02", "Okavango Copper",       "$22.6M", "FAT",             "-3 wks",   "Amber"],
                ["SGSB-1006-PKG-01", "Cape Town Desal",       "$14.2M", "RFQ",             "-8 wks",   "Red"],
                ["SGSB-1007-PKG-02", "Saldanha Hydrogen",     "$31.5M", "Manufacturing",   "On track", "Green"],
                ["SGSB-1008-PKG-01", "Lubumbashi Refinery",   "$4.1M",  "Commissioning",   "+1 wk",    "Green"],
            ],
        },
        aiPrompts: [
            "Which packages should be escalated to steerco this week?",
            "Predict the top three procurement bottlenecks over the next 60 days.",
        ],
    },
    {
        key: "supplier_intelligence",
        name: "Supplier Intelligence™",
        section: "Procurement",
        icon: "Award",
        tagline: "Supplier performance intelligence across the procurement portfolio.",
        purpose: "Historical supplier delivery, quality, commercial and safety performance rolled into a single executive-grade scorecard with AI recommendations.",
        kpis: [
            { label: "Suppliers", value: "148" },
            { label: "Preferred", value: "62" },
            { label: "OTIF (portfolio)", value: "88.4%" },
            { label: "Avg Score", value: "4.2 / 5" },
        ],
        table: {
            title: "Top Suppliers by Performance",
            columns: ["Supplier", "Category", "Delivery", "Quality", "Commercial", "Overall"],
            rows: [
                ["ABB",              "MV Switchgear",     "94%", "4.6", "4.4", "4.5"],
                ["Weir Minerals",    "Slurry Pumps",      "91%", "4.5", "4.2", "4.4"],
                ["Metso Outotec",    "Mill / Crusher",    "88%", "4.4", "4.1", "4.3"],
                ["Konecranes",       "Cranes",            "96%", "4.7", "4.3", "4.5"],
                ["Atlas Copco",      "Compressors",       "89%", "4.5", "4.0", "4.3"],
                ["Casale",           "Ammonia Packages",  "82%", "4.6", "3.9", "4.2"],
                ["DuPont",           "Membranes",         "84%", "4.7", "3.8", "4.3"],
                ["Siemens",          "Automation",        "90%", "4.5", "4.1", "4.4"],
            ],
        },
        aiPrompts: [
            "Which suppliers have improving trends this year?",
            "Recommend alternative suppliers for slurry pump duty > 100 m³/h.",
        ],
    },

    // =========================================================================
    // COMMERCIAL
    // =========================================================================
    {
        key: "contract_intel",
        name: "Contract Intelligence™",
        section: "Commercial",
        icon: "FileSignature",
        tagline: "Convert contracting information into live executive intelligence.",
        purpose: "Live view of contract status, claims, variations, commercial risks, milestones, expiry, retention, performance, executive alerts and AI recommendations.",
        kpis: [
            { label: "Active Contracts", value: "42" },
            { label: "Open Claims", value: "6" },
            { label: "Variations (30d)", value: "18" },
            { label: "Expiring < 60d", value: "4" },
        ],
        table: {
            title: "Executive Contract Radar",
            columns: ["Contract", "Contractor", "Value", "Risk", "Alert"],
            rows: [
                ["SGSB-1003-CTR-001", "Aveng Grinaker-LTA", "$36.2M", "High",   "Claim quantum exceeds contingency"],
                ["SGSB-1006-CTR-002", "WBHO Construction",  "$28.4M", "High",   "Expires in 42 days · renewal not started"],
                ["SGSB-1002-CTR-001", "Murray & Roberts",   "$41.8M", "Medium", "3 open variations pending resolution"],
                ["SGSB-1005-CTR-002", "Stefanutti Stocks",  "$22.6M", "Medium", "Retention overdue for release"],
                ["SGSB-1001-CTR-001", "Group Five Africa",  "$52.1M", "Low",    "Performance ahead of programme"],
                ["SGSB-1004-CTR-002", "Concor Engineering", "$18.9M", "Low",    "Milestone certificate pending"],
            ],
        },
        aiPrompts: [
            "Which contracts require executive attention this week?",
            "Summarise commercial exposure by contractor.",
        ],
    },
    {
        key: "cop",
        name: "Contracting Operating Plan (COP)",
        section: "Commercial",
        icon: "FileStack",
        tagline: "Interactive commercial dashboard for the contracting lifecycle.",
        purpose: "Packaging strategy, award schedule, contract status, claims, variations, risk, commercial exposure and executive actions.",
        kpis: [
            { label: "Contracts Live", value: "42" },
            { label: "Awards This Q", value: "8" },
            { label: "Total Exposure", value: "$614M" },
            { label: "Claims Value", value: "$18.2M" },
        ],
        table: {
            title: "Award Schedule",
            columns: ["Contract", "Project", "Package", "Strategy", "Award Target", "Status"],
            rows: [
                ["SGSB-1002-CTR-002", "Rustenburg Smelter", "Refractory Supply",  "Direct award",    "2026-03-15", "Under Evaluation"],
                ["SGSB-1005-CTR-003", "Okavango Copper",    "Solvent Extraction", "Competitive bid", "2026-04-02", "Under Evaluation"],
                ["SGSB-1006-CTR-002", "Cape Town Desal",    "Membrane Supply",    "Framework",       "2026-03-28", "RFQ"],
                ["SGSB-1007-CTR-001", "Saldanha Hydrogen",  "Compressor Package", "Competitive bid", "2026-05-10", "Draft"],
                ["SGSB-1001-CTR-002", "Kolomela",           "Motor Package",      "Competitive bid", "2026-03-05", "Under Evaluation"],
                ["SGSB-1003-CTR-002", "Nampula Ammonia",    "FEED Study",         "Direct award",    "2026-03-20", "Under Evaluation"],
            ],
        },
        aiPrompts: [
            "Which awards must complete before the end of Q1?",
            "Summarise commercial exposure by contractor for the board pack.",
        ],
    },
    {
        key: "claims_intelligence",
        name: "Claims Intelligence™",
        section: "Commercial",
        icon: "AlertTriangle",
        tagline: "Claims and variations intelligence with early-warning alerts.",
        purpose: "Track open claims, variations, retentions, commercial risk and expiry with executive alerts, AI recommendations and historical resolution patterns.",
        kpis: [
            { label: "Open Claims", value: "6" },
            { label: "Claims Value", value: "$18.2M" },
            { label: "Variations (30d)", value: "18" },
            { label: "Avg Resolution", value: "34 d" },
        ],
        table: {
            title: "Open Claims Register",
            columns: ["Claim", "Contract", "Type", "Amount", "Age (d)", "Recommendation"],
            rows: [
                ["SGSB-CLM-2026-004", "SGSB-1003-CTR-001", "Delay claim",    "$6.4M",  "48", "Negotiate ex-gratia + programme reset"],
                ["SGSB-CLM-2026-005", "SGSB-1002-CTR-001", "Variation",      "$1.8M",  "22", "Approve — within contingency"],
                ["SGSB-CLM-2026-006", "SGSB-1005-CTR-002", "Retention",      "$0.9M",  "62", "Release retention Q2"],
                ["SGSB-CLM-2026-007", "SGSB-1006-CTR-001", "Scope creep",    "$4.2M",  "18", "Defend — outside contract scope"],
                ["SGSB-CLM-2026-008", "SGSB-1003-CTR-002", "Standing time",  "$3.1M",  "31", "Negotiate — partial merit"],
                ["SGSB-CLM-2026-009", "SGSB-1004-CTR-001", "Design change",  "$1.8M",  "12", "Approve — client-initiated"],
            ],
        },
        aiPrompts: [
            "Which claims are most likely to escalate to dispute?",
            "Recommend a claims strategy for SGSB-CLM-2026-004.",
        ],
    },

    // =========================================================================
    // KNOWLEDGE
    // =========================================================================
    {
        key: "digitisation",
        name: "Enterprise Knowledge Digitisation™",
        section: "Knowledge",
        icon: "ScanLine",
        tagline: "Digitise decades of engineering knowledge into a searchable corporate archive.",
        purpose: "Convert legacy archives, large-format drawings, vendor manuals and inspection reports into an intelligent, searchable knowledge base.",
        kpis: [
            { label: "Pages Digitised", value: "2,412,880" },
            { label: "Drawings Digitised", value: "184,633" },
            { label: "OCR Completion", value: "92%" },
            { label: "Projects Archived", value: "312" },
        ],
        table: {
            title: "Active Digitisation Batches",
            columns: ["Batch", "Client", "Documents", "Format", "OCR", "Status"],
            rows: [
                ["BATCH-2026-014", "Kumba Iron Ore",   "18,204",  "A0 Drawings",     "88%",  "Processing"],
                ["BATCH-2026-015", "Anglo Platinum",   "4,120",   "Vendor Manuals",  "100%", "Complete"],
                ["BATCH-2026-016", "OCP Africa",       "12,540",  "Contracts",       "72%",  "OCR"],
                ["BATCH-2026-017", "Vale Moçambique",  "8,910",   "P&IDs",           "95%",  "QA"],
                ["BATCH-2026-018", "Khoemacau Copper", "3,350",   "Data Sheets",     "100%", "Complete"],
                ["BATCH-2026-019", "Hive Hydrogen",    "6,780",   "Specifications",  "45%",  "Scanning"],
            ],
        },
        aiPrompts: [
            "Recommend the next five archives to prioritise for digitisation based on business value.",
            "Identify duplicate document sets across the current batches.",
        ],
    },
    {
        key: "migration",
        name: "Knowledge Migration™",
        section: "Knowledge",
        icon: "ArrowLeftRight",
        tagline: "Move legacy engineering knowledge into the Enterprise Knowledge Repository.",
        purpose: "Systematically migrate SharePoint sites, network drives and legacy servers with metadata preservation, duplicate detection and version control.",
        kpis: [
            { label: "Projects Migrated", value: "218" },
            { label: "Files Migrated", value: "1.94M" },
            { label: "Duplicates Detected", value: "42,610" },
            { label: "Completion", value: "88%" },
        ],
        table: {
            title: "Migration Waves",
            columns: ["Wave", "Source", "Files", "Duplicates", "Errors", "Status"],
            rows: [
                ["WAVE-01", "SharePoint · Projects", "612,400", "18,240", "24", "Complete"],
                ["WAVE-02", "NAS · Engineering",     "488,120", "14,910", "12", "Complete"],
                ["WAVE-03", "Legacy Server · SGS-04","318,720", "6,120",  "38", "In progress"],
                ["WAVE-04", "SharePoint · Tenders",  "218,600", "2,140",  "8",  "Validation"],
                ["WAVE-05", "NAS · Vendor Docs",     "301,540", "1,200",  "0",  "Queued"],
            ],
        },
        aiPrompts: [
            "Which migration wave has the highest data-quality risk and why?",
            "Recommend a duplicate-resolution policy for cross-source overlaps.",
        ],
    },
    {
        key: "repository",
        name: "Enterprise Knowledge Repository™",
        section: "Knowledge",
        icon: "Database",
        tagline: "The single corporate engineering knowledge base.",
        purpose: "Central repository for drawings, data sheets, specifications, vendor packages, calculations, lessons learned, procurement packages and QA documentation.",
        kpis: [
            { label: "Total Assets", value: "2.41M" },
            { label: "Full-Text Indexed", value: "99.2%" },
            { label: "AI Search Coverage", value: "100%" },
            { label: "Avg Query Time", value: "6.2 s" },
        ],
        table: {
            title: "Repository Composition",
            columns: ["Asset Class", "Volume", "Growth (12m)", "Reuse Rate", "Owners"],
            rows: [
                ["Drawings",              "184,633",  "+18.4%", "52%", "Engineering"],
                ["Data Sheets",           "18,940",   "+22.1%", "68%", "Engineering"],
                ["Specifications",        "9,414",    "+11.8%", "47%", "Engineering"],
                ["Vendor Packages",       "12,304",   "+7.5%",  "41%", "Procurement"],
                ["Engineering Calcs",     "23,180",   "+14.9%", "38%", "Engineering"],
                ["Lessons Learned",       "1,842",    "+31.6%", "N/A", "PMO"],
                ["Procurement Packages",  "4,610",    "+9.2%",  "44%", "Procurement"],
                ["Contract Templates",    "612",      "+6.1%",  "62%", "Commercial"],
                ["Method Statements",     "3,908",    "+12.3%", "39%", "Construction"],
                ["QA Documentation",      "42,110",   "+16.2%", "22%", "Quality"],
            ],
        },
        aiPrompts: [
            "Find similar Gold Plant projects.",
            "Reuse Harmony Gold mechanical specifications for a new 4Mtpa plant.",
        ],
    },
    {
        key: "transformation",
        name: "Knowledge Transformation Programme™",
        section: "Knowledge",
        icon: "Wand2",
        tagline: "Transform historical documentation into reusable corporate intelligence.",
        purpose: "Programmatic identification of reusable designs, standard calculations, preferred vendors, historical pricing, standard work packages and best practices.",
        kpis: [
            { label: "Reusable Assets Identified", value: "84,120" },
            { label: "Standard Templates", value: "612" },
            { label: "Preferred Vendors", value: "148" },
            { label: "Pricing Datapoints", value: "218,900" },
        ],
        table: {
            title: "Transformation Pipeline",
            columns: ["Programme", "Source Volume", "Reusable Yield", "Confidence", "Owner"],
            rows: [
                ["Standard Designs",       "48,120",  "18,940 (39%)",  "High",   "Engineering"],
                ["Standard Calculations",  "23,180",  "8,412 (36%)",   "High",   "Engineering"],
                ["Standard Specifications","9,414",   "4,110 (44%)",   "High",   "Engineering"],
                ["Preferred Vendors",      "612",     "148 (24%)",     "Medium", "Procurement"],
                ["Historical Pricing",     "218,900", "218,900 (100%)","High",   "Commercial"],
                ["Lessons Learned",        "1,842",   "1,842 (100%)",  "High",   "PMO"],
                ["Standard Work Packages", "3,180",   "1,412 (44%)",   "Medium", "Construction"],
                ["Best Practices",         "418",     "418 (100%)",    "High",   "PMO"],
            ],
        },
        aiPrompts: [
            "Which reusable design assets should be promoted to enterprise standards this quarter?",
            "Summarise lessons learned from Harmony Gold projects.",
        ],
    },
    {
        key: "ocr",
        name: "OCR Engine™",
        section: "Knowledge",
        icon: "ScanText",
        tagline: "Convert scanned images into searchable engineering documents.",
        purpose: "OCR across engineering drawings, contracts, vendor manuals, inspection reports and large-format A0 drawings. Search across the full corpus in seconds.",
        kpis: [
            { label: "OCR Coverage", value: "92%" },
            { label: "Pages / Day", value: "48,120" },
            { label: "Character Accuracy", value: "99.4%" },
            { label: "Search Latency", value: "6.2 s" },
        ],
        table: {
            title: "OCR Throughput",
            columns: ["Document Class", "Volume (30d)", "Accuracy", "Median Page Time"],
            rows: [
                ["A0 Engineering Drawings", "38,120", "98.9%", "8.4 s"],
                ["Contracts",              "18,420", "99.6%", "2.1 s"],
                ["Vendor Manuals",         "24,180", "99.4%", "3.2 s"],
                ["Inspection Reports",     "12,610", "99.2%", "2.6 s"],
                ["Data Sheets",            "9,240",  "99.7%", "1.8 s"],
                ["Method Statements",      "5,820",  "99.5%", "2.4 s"],
            ],
        },
        aiPrompts: [
            "Search for 'Rip Tank' across the entire OCR corpus.",
            "Which document classes have the lowest OCR accuracy this month?",
        ],
    },
    {
        key: "classification",
        name: "AI Classification™",
        section: "Knowledge",
        icon: "Tags",
        tagline: "Automatically classify every document.",
        purpose: "Every ingested document is auto-tagged by commodity, discipline, document type, equipment class, revision, client and project.",
        kpis: [
            { label: "Docs Auto-Classified", value: "2.41M" },
            { label: "Taxonomy Nodes", value: "1,842" },
            { label: "Precision", value: "97.8%" },
            { label: "Recall", value: "96.2%" },
        ],
        table: {
            title: "Recent Classifications",
            columns: ["Document", "Commodity", "Discipline", "Equipment", "Client", "Rev"],
            rows: [
                ["SGSB-KB-DS-004120",   "Gold",     "Mechanical",   "Rip Tank",         "Harmony Gold",    "Rev C"],
                ["SGSB-KB-DWG-018204",  "Iron Ore", "Piping",       "Cyclone Battery",  "Kumba Iron Ore",  "Rev B"],
                ["SGSB-KB-SPEC-000912", "Copper",   "Electrical",   "MV Switchgear",    "Khoemacau",       "Rev A"],
                ["SGSB-KB-PFD-002014",  "Ammonia",  "Process",      "Reactor Train",    "OCP Africa",      "Rev B"],
                ["SGSB-KB-BOQ-000542",  "Coal",     "Structural",   "Screen Structure", "Vale Moçambique", "Rev C"],
                ["SGSB-KB-LL-000188",   "Cobalt",   "Construction", "Refinery Train",   "ERG Africa",      "Rev A"],
            ],
        },
        aiPrompts: [
            "Classify these unlabelled documents into the SGS Bateman taxonomy.",
            "Which taxonomy nodes have the highest annotation drift?",
        ],
    },
    {
        key: "ip_protection",
        name: "Intellectual Capital Protection™",
        section: "Knowledge",
        icon: "ShieldCheck",
        tagline: "Protect SGS Bateman engineering knowledge.",
        purpose: "Track knowledge ownership, reusable assets, lessons learned, subject-matter experts, knowledge gaps, retirement risk, knowledge utilisation and knowledge growth.",
        kpis: [
            { label: "Knowledge Owners", value: "148" },
            { label: "SMEs Registered", value: "62" },
            { label: "Retirement Risk", value: "9%" },
            { label: "Coverage", value: "94%" },
        ],
        table: {
            title: "Knowledge Ownership Register",
            columns: ["Domain", "Owner", "SMEs", "Reusable Assets", "Retirement Risk"],
            rows: [
                ["Gold CIP / CIL",            "K. Molefe",    "6", "1,240", "Low"],
                ["Iron Ore Beneficiation",    "A. Steyn",     "4", "1,140", "Medium"],
                ["Copper Solvent Extraction", "P. Naidoo",    "3", "820",   "High"],
                ["Ammonia / Fertiliser",      "T. Adeyemi",   "5", "612",   "Medium"],
                ["Water Treatment",           "E. Marchetti", "4", "418",   "Low"],
                ["Green Hydrogen",            "K. Molefe",    "2", "144",   "High"],
                ["Cobalt Refining",           "T. Adeyemi",   "3", "216",   "Medium"],
            ],
        },
        aiPrompts: [
            "Which knowledge domains carry the highest retirement risk?",
            "Recommend an SME succession plan for Copper Solvent Extraction.",
        ],
    },
    {
        key: "library_growth",
        name: "Knowledge Library Growth™",
        section: "Knowledge",
        icon: "TrendingUp",
        tagline: "Track corporate knowledge growth and reuse performance.",
        purpose: "Projects archived, engineering documents, data sheets, drawings, lessons learned, reusable templates, knowledge reuse rate, growth over time, hours saved and cost savings.",
        kpis: [
            { label: "Projects Archived", value: "312" },
            { label: "Reuse Rate", value: "47.3%" },
            { label: "Growth (12m)", value: "+24.8%" },
            { label: "Est. Cost Saved", value: "$32.76M" },
        ],
        table: {
            title: "Quarterly Growth",
            columns: ["Quarter", "Docs (M)", "Reuse %", "Hrs Saved", "Est. Value"],
            rows: [
                ["Q1 2024", "0.64", "12.1%", "18,400",  "$2.76M"],
                ["Q2 2024", "0.81", "17.4%", "34,900",  "$5.23M"],
                ["Q3 2024", "1.04", "22.8%", "61,200",  "$9.18M"],
                ["Q4 2024", "1.29", "27.2%", "92,800",  "$13.92M"],
                ["Q1 2025", "1.54", "31.5%", "118,400", "$17.76M"],
                ["Q2 2025", "1.78", "36.9%", "148,700", "$22.30M"],
                ["Q3 2025", "2.04", "41.4%", "178,200", "$26.73M"],
                ["Q4 2025", "2.27", "44.9%", "201,600", "$30.24M"],
                ["Q1 2026", "2.41", "47.3%", "218,400", "$32.76M"],
            ],
        },
        aiPrompts: [
            "Project 12-month knowledge library growth and financial impact.",
            "Which asset classes are growing fastest and why?",
        ],
    },
    {
        key: "services",
        name: "Enterprise Digitisation & Knowledge Migration Services™",
        section: "Knowledge",
        icon: "LifeBuoy",
        tagline: "End-to-end services to bring any organisation onto the platform.",
        purpose: "Archive assessment, scanning, OCR, AI classification, migration, validation, repository creation, training and support — delivered as a services engagement.",
        kpis: [
            { label: "Engagement Types", value: "9" },
            { label: "Avg Ramp-Up", value: "6 wks" },
            { label: "Client Success", value: "100%" },
            { label: "Reference Deployments", value: "18" },
        ],
        table: {
            title: "Service Catalogue",
            columns: ["Service", "Duration", "Deliverables", "SLA"],
            rows: [
                ["Archive Assessment",  "2 weeks",  "Baseline report, prioritisation",          "Executive brief within 10 business days"],
                ["Scanning",            "Ongoing",  "Batch scanning at scale, dual-shift",      "48-hour turnaround per batch"],
                ["OCR Processing",      "Ongoing",  "Searchable PDFs, character accuracy 99.4%","72-hour SLA"],
                ["AI Classification",   "Ongoing",  "Full taxonomy tagging",                    "Precision ≥ 96%"],
                ["Migration",           "6-12 wks", "SharePoint / NAS / legacy migration",      "Zero-loss guarantee"],
                ["Validation",          "2 wks",    "Independent audit + certification",        "Signed validation letter"],
                ["Repository Creation", "4 wks",    "Client tenant provisioning",               "Go-live within 4 weeks"],
                ["Training",            "2 wks",    "Role-based training + certification",      "≥ 90% pass rate"],
                ["Support",             "Ongoing",  "24×7 executive support desk",              "1-hour executive response"],
            ],
        },
        aiPrompts: [
            "Draft a scope of work for archive assessment for a new client.",
            "Recommend a phased digitisation programme for a 40-year engineering archive.",
        ],
    },

    // =========================================================================
    // EXECUTIVE
    // =========================================================================
    {
        key: "info_reuse",
        name: "Enterprise Information Reuse™",
        section: "Executive",
        icon: "Layers",
        tagline: "Capture Once. Use Many Times.",
        purpose: "When an engineer updates information once, the platform automatically updates every downstream surface — dashboards, reports, alerts, KPIs, executive AI context.",
        kpis: [
            { label: "Sources of Truth", value: "142" },
            { label: "Downstream Surfaces", value: "612" },
            { label: "Update Latency", value: "< 3 s" },
            { label: "Duplicate Reports", value: "0" },
        ],
        table: {
            title: "Downstream Propagation",
            columns: ["Data Point", "Sources", "Downstream Surfaces", "Latency"],
            rows: [
                ["Project Progress %",      "1", "Executive DB, Project DB, Weekly Rpt, Portfolio KPIs, AI Context", "< 2 s"],
                ["Contract Value",          "1", "Executive DB, Commercial DB, Board Rpt, Alerts, AI Context",       "< 2 s"],
                ["Tender Status",           "1", "Executive DB, Tender DB, Weekly Rpt, Alerts",                       "< 2 s"],
                ["Engineering Deliverable", "1", "Engineering DB, Project DB, Weekly Rpt",                            "< 3 s"],
                ["Cost Variance",           "1", "Executive DB, Commercial DB, Cost Forecast Rpt, AI Context",        "< 2 s"],
                ["Schedule Variance",       "1", "Executive DB, Project DB, Schedule Rpt, AI Context",                "< 2 s"],
            ],
        },
        aiPrompts: [
            "Which data points still require manual duplication?",
            "Show the propagation graph for 'Project Progress %'.",
        ],
    },
    {
        key: "enterprise_search",
        name: "Enterprise Search™",
        section: "Executive",
        icon: "Search",
        tagline: "One intelligent search across the entire corporate knowledge base.",
        purpose: "Find projects, drawings, data sheets, contracts, lessons learned, specifications, calculations and vendor manuals with similarity-ranked results.",
        kpis: [
            { label: "Corpus Size", value: "2.41M" },
            { label: "Avg Latency", value: "6.2 s" },
            { label: "Precision", value: "97.8%" },
            { label: "Daily Queries", value: "1,240" },
        ],
        table: {
            title: "Recent Query Categories",
            columns: ["Category", "Queries (30d)", "Avg Latency", "Top Term"],
            rows: [
                ["Drawings",       "8,120", "5.8 s", "Rip Tank P&ID"],
                ["Data Sheets",    "6,410", "4.2 s", "Slurry Pump"],
                ["Specifications", "4,180", "6.4 s", "SS316L slurry"],
                ["Contracts",      "3,140", "7.1 s", "Retention clause"],
                ["Lessons Learned","2,980", "5.9 s", "Refractory life"],
                ["Vendor Manuals", "2,410", "8.2 s", "Weir slurry pump"],
            ],
        },
        aiPrompts: [
            "Find similar Gold Plant projects.",
            "Show all contracts with retention overdue > 60 days.",
        ],
    },
    {
        key: "executive_ai",
        name: "Executive AI™",
        section: "Executive",
        icon: "Sparkles",
        tagline: "Executive AI advisor available from every screen.",
        purpose: "GPT-5.2 grounded on the entire SGS Bateman portfolio and knowledge base. Available from the Executive Brief panel on every page — ask about projects, tenders, procurement, contracts, engineering, lessons learned or portfolio risk.",
        kpis: [
            { label: "Queries (30d)", value: "3,140" },
            { label: "Grounded On", value: "2.41M docs" },
            { label: "Response Time", value: "< 8 s" },
            { label: "Accuracy", value: "97.1%" },
        ],
        table: {
            title: "Common Executive Questions",
            columns: ["Category", "Example", "Avg Response Time"],
            rows: [
                ["Portfolio Risk",       "Which projects are at risk?",                    "6.4 s"],
                ["Tender Attention",     "Which tenders require immediate attention?",     "7.1 s"],
                ["Cost Variance",        "Explain cost variances on Nampula Ammonia.",     "8.2 s"],
                ["Similar Projects",     "Find similar gold plant projects.",              "5.8 s"],
                ["Executive Brief",      "Generate today's Executive Morning Brief.",      "12.4 s"],
                ["Board Report",         "Generate a monthly board report.",               "18.6 s"],
                ["Procurement Strategy", "Generate a procurement strategy for a new project.","9.2 s"],
                ["Lessons Applied",      "Show lessons learned applicable to a new project.","6.8 s"],
            ],
        },
        aiPrompts: [
            "Generate today's Executive Morning Brief for the SGS Bateman portfolio.",
            "Predict portfolio completion trends for the next two quarters.",
        ],
    },
    {
        key: "ai_reuse_advisor",
        name: "AI Reuse Advisor™",
        section: "Engineering",
        icon: "Wand2",
        tagline: "One click. New project baseline.",
        purpose: "Configure a new project — the platform matches similar historical delivery, recommends reusable engineering, generates a project baseline and estimates hours and cost saved. Exports a board-ready executive bid pack in seconds.",
        kpis: [
            { label: "Baseline Time", value: "< 8 s" },
            { label: "Confidence", value: "91%" },
            { label: "Auto-Populated", value: "83%" },
            { label: "Hours Saved / Project", value: "2,180" },
        ],
        table: { title: "Baseline History", columns: ["Opportunity", "Type", "Baseline Time", "Hrs Saved", "Bid Won"], rows: [
            ["SGSB-OPP-2026-042", "Gold CIP · 4Mtpa", "6.8 s", "2,180", "Awarded"],
            ["SGSB-OPP-2026-043", "PGM Smelter",     "7.4 s", "1,640", "Under Evaluation"],
            ["SGSB-OPP-2026-044", "Copper Leach",    "6.2 s", "1,940", "Awarded"],
            ["SGSB-OPP-2026-045", "Coal Beneficiation","5.9 s","1,140", "Awarded"],
        ]},
        aiPrompts: [
            "Which historical projects should baseline a new Copper Leach opportunity?",
            "Explain the hours-saved calculation for the last baseline run.",
        ],
    },
    {
        key: "corporate_standards",
        name: "Corporate Engineering Standards™",
        section: "Engineering",
        icon: "BookMarked",
        tagline: "Approved corporate engineering rules — design practices, calculations, checklists.",
        purpose: "The governed, versioned set of engineering standards, design practices, corporate specifications, approved calculations, preferred templates, approved design rules and engineering checklists.",
        kpis: [
            { label: "Standards Published", value: "612" },
            { label: "Approved Calcs", value: "218" },
            { label: "Design Rules", value: "148" },
            { label: "Compliance", value: "98%" },
        ],
        table: { title: "Governed Standards", columns: ["Standard", "Type", "Version", "Approved", "Owner"], rows: [
            ["STD-DR-018 · Slurry Pipe Erosion",  "Design Rule",   "v3.2", "2025-11", "Piping SME"],
            ["STD-CALC-024 · Foundation Loading", "Calculation",   "v2.4", "2025-09", "Civil SME"],
            ["STD-CHK-011 · Weld QA",             "Checklist",     "v5.0", "2025-12", "Quality SME"],
            ["STD-SPEC-041 · MV Switchgear",      "Specification", "v4.1", "2026-01", "Electrical SME"],
            ["STD-DR-062 · Instrument Loop",      "Design Rule",   "v2.3", "2025-08", "I&C SME"],
        ]},
        aiPrompts: [
            "Which corporate standards are due for revision this quarter?",
            "Verify compliance of the Nampula Ammonia FEED against approved standards.",
        ],
    },
    {
        key: "knowledge_capture_workflow",
        name: "Knowledge Capture Workflow™",
        section: "Knowledge",
        icon: "Repeat",
        tagline: "Every completed project becomes reusable corporate knowledge.",
        purpose: "The formal pipeline: Project Completion → Lessons Learned → Engineering Validation → AI Classification → Knowledge Repository → Available for Future Projects. Continuous, governed and measured.",
        kpis: [
            { label: "Projects In-Flight", value: "8" },
            { label: "Awaiting Capture", value: "3" },
            { label: "Avg Capture Cycle", value: "14 d" },
            { label: "Reuse Rate After Capture", value: "78%" },
        ],
        table: { title: "Capture Pipeline", columns: ["Stage", "Items", "Avg Duration", "SLA"], rows: [
            ["Project Completion",       "8",  "—",     "Trigger"],
            ["Lessons Learned",          "5",  "3 d",   "5 d"],
            ["Engineering Validation",   "4",  "5 d",   "7 d"],
            ["AI Classification",        "12", "< 1 h", "24 h"],
            ["Repository Ingestion",     "12", "< 1 h", "24 h"],
            ["Available for Reuse",      "12", "immediate", "—"],
        ]},
        aiPrompts: [
            "Which projects are overdue at the Lessons Learned stage?",
            "Recommend improvements to the knowledge capture cycle time.",
        ],
    },
    {
        key: "retirement_risk",
        name: "Retirement Risk Intelligence™",
        section: "Knowledge",
        icon: "ShieldAlert",
        tagline: "Identify critical SMEs and protect corporate knowledge continuity.",
        purpose: "Automatically identifies engineers approaching retirement, critical SMEs, knowledge still uncaptured, required succession planning and knowledge transfer progress.",
        kpis: [
            { label: "Critical SMEs", value: "18" },
            { label: "Retirement < 3 yr", value: "6" },
            { label: "Uncaptured Domains", value: "3" },
            { label: "Succession Coverage", value: "82%" },
        ],
        table: { title: "Retirement Risk Register", columns: ["SME", "Domain", "Retire In", "Uncaptured", "Successor"], rows: [
            ["P. Naidoo",   "Copper Solvent Extraction", "18 mo", "12%",  "In training"],
            ["K. Molefe",   "Gold CIP / CIL",            "36 mo", "6%",   "Ready"],
            ["A. Steyn",    "Iron Ore Beneficiation",    "48 mo", "8%",   "Ready"],
            ["T. Adeyemi",  "Cobalt Refining",           "24 mo", "18%",  "In training"],
            ["E. Marchetti","Water Treatment",           "60 mo", "4%",   "Ready"],
        ]},
        aiPrompts: [
            "Which SMEs need urgent knowledge capture and succession planning?",
            "Draft a 12-month succession plan for Copper Solvent Extraction.",
        ],
    },
    {
        key: "reference_client_portal",
        name: "Reference Client Portal™",
        section: "Executive",
        icon: "Rocket",
        tagline: "Commercial portal showcasing digitisation, migration and delivery outcomes.",
        purpose: "Publish digitisation, knowledge migration, knowledge transformation and delivery intelligence outcomes — implementation methodology, case studies and measurable business outcomes — to prospective clients.",
        kpis: [
            { label: "Case Studies", value: "18" },
            { label: "Reference Clients", value: "12" },
            { label: "Portfolio Value", value: "$3.4B" },
            { label: "Client NPS", value: "68" },
        ],
        table: { title: "Reference Case Studies", columns: ["Client", "Sector", "Outcome", "Reuse %", "Cost Saved"], rows: [
            ["Harmony Gold",       "Gold",     "4Mtpa CIP baseline deployed",      "62%", "$4.2M"],
            ["Kumba Iron Ore",     "Iron Ore", "Concentrator upgrade fast-track",  "58%", "$3.8M"],
            ["Anglo Platinum",     "PGM",     "Smelter modernisation",             "44%", "$2.1M"],
            ["OCP Africa",         "Chemicals","Ammonia FEED programme",           "38%", "$1.9M"],
            ["Vale Moçambique",    "Coal",     "Beneficiation debottlenecking",     "51%", "$1.4M"],
            ["Khoemacau Copper",   "Copper",   "SX contactor expansion",           "48%", "$2.6M"],
        ]},
        aiPrompts: [
            "Which case studies are most relevant to a prospective gold client?",
            "Draft an executive summary of the SGS Bateman digitisation track record.",
        ],
    },
    {
        key: "board_pack_generator",
        name: "Board Pack Generator™",
        section: "Executive",
        icon: "FileStack",
        tagline: "Compose and export a board-ready executive pack in PDF, Word or Excel.",
        purpose: "Select the sections you want, and the platform composes an executive committee or board pack with the SGS Bateman cover, header, footer, page numbering and confidentiality watermark.",
        kpis: [
            { label: "Sections", value: "10" },
            { label: "Formats", value: "3" },
            { label: "Avg Prep Time", value: "< 15 s" },
            { label: "Historic Packs", value: "148" },
        ],
        table: { title: "Recent Board Packs", columns: ["Pack", "Sections", "Format", "Issued", "Audience"], rows: [
            ["Feb 2026 Board Pack",           "10", "PDF",  "2026-02-05", "Board"],
            ["Q4 2025 EXCO Review",           "8",  "PDF",  "2026-01-14", "Executive"],
            ["Portfolio Review · Jan 2026",   "6",  "DOCX", "2026-01-20", "Executive"],
            ["Commercial Committee · Jan 26", "5",  "XLSX", "2026-01-22", "Committee"],
        ]},
        aiPrompts: [
            "Which sections belong in this month's board pack given the current portfolio state?",
            "Draft the Executive Commentary section for the Feb 2026 board pack.",
        ],
    },
    {
        key: "knowledge_lifecycle",
        name: "Enterprise Knowledge Lifecycle™",
        section: "Knowledge",
        icon: "GitBranch",
        tagline: "Every engineering document moves through 13 governed stages.",
        purpose: "Capture → Validate → Digitise → OCR → AI Classification → Metadata Enrichment → Knowledge Repository → Similarity Analysis → Engineering Reuse → Executive Intelligence → Future Project Reuse → Lessons Learned → Continuous Growth.",
        kpis: [
            { label: "Stages", value: "13" },
            { label: "Items In-Flight", value: "3,240" },
            { label: "Cycle Time", value: "14 d" },
            { label: "Automation Coverage", value: "87%" },
        ],
        table: { title: "Lifecycle Throughput (30 d)", columns: ["Stage", "In", "Out", "Automation", "SLA"], rows: [
            ["Capture",              "1,840", "1,820", "auto",  "24 h"],
            ["Validate",             "1,820", "1,800", "assisted","48 h"],
            ["Digitise",             "1,800", "1,780", "auto",  "72 h"],
            ["OCR",                  "1,780", "1,780", "auto",  "48 h"],
            ["AI Classification",    "1,780", "1,780", "auto",  "24 h"],
            ["Metadata Enrichment",  "1,780", "1,780", "auto",  "24 h"],
            ["Repository Ingestion", "1,780", "1,780", "auto",  "24 h"],
            ["Similarity Analysis",  "1,780", "1,780", "auto",  "24 h"],
            ["Engineering Reuse",    "1,780", "1,240", "assisted","N/A"],
            ["Lessons Learned",      "412",   "412",  "assisted","5 d"],
        ]},
        aiPrompts: [
            "Which stage is the bottleneck this cycle?",
            "Predict lifecycle throughput for next quarter.",
        ],
    },
    {
        key: "sharepoint",
        name: "Live SharePoint Integration™",
        section: "Knowledge",
        icon: "Cloud",
        tagline: "Continue using SharePoint — the platform turns it into searchable engineering intelligence.",
        purpose: "Production-grade Microsoft 365 SharePoint integration with OAuth, scheduled + near real-time synchronisation, version preservation, metadata + permissions, duplicate detection, incremental updates, deleted-doc handling, ingestion health, sync audit trail, and immediate AI classification after ingestion.",
        kpis: [
            { label: "Sites Monitored", value: "18" },
            { label: "Documents Indexed", value: "612,400" },
            { label: "Sync Interval", value: "15 min" },
            { label: "Auto-Classified", value: "98%" },
        ],
        table: { title: "Ingestion Health (24 h)", columns: ["Metric", "Value"], rows: [
            ["Sync jobs",             "96"],
            ["Documents processed",   "4,860"],
            ["New",                   "1,240"],
            ["Updated",               "3,120"],
            ["Duplicates detected",   "412"],
            ["Errors",                "0"],
            ["Avg sync duration",     "62 s"],
        ]},
        aiPrompts: [
            "Which SharePoint sites are approaching duplicate saturation?",
            "Recommend the next SharePoint tenant to onboard.",
        ],
    },
    {
        key: "asset_intelligence",
        name: "Enterprise Engineering Asset Intelligence™",
        section: "Engineering",
        icon: "Boxes",
        tagline: "The repository recognises engineering assets — not just documents.",
        purpose: "Every asset is linked to its historical projects, drawings, vendors, performance history, specifications, data sheets, calculations and recommended reuse opportunities.",
        kpis: [
            { label: "Asset Classes", value: "18" },
            { label: "Instances Tracked", value: "84,120" },
            { label: "Linked Documents", value: "1.42M" },
            { label: "Vendor Refs", value: "148" },
        ],
        table: { title: "Asset Catalogue", columns: ["Asset", "Instances", "Drawings", "Data Sheets", "Vendors"], rows: [
            ["Pumps",                  "1,842", "3,180", "1,240", "18"],
            ["Tanks",                  "612",   "1,120", "612",   "9"],
            ["Thickener Systems",      "84",    "410",   "84",    "6"],
            ["Crushers",               "148",   "620",   "148",   "6"],
            ["Mills",                  "68",    "312",   "68",    "5"],
            ["Cyclones",               "412",   "820",   "412",   "6"],
            ["Piping Classes",         "612",   "1,240", "820",   "12"],
            ["Valves",                 "2,180", "3,410", "1,840", "16"],
            ["Motors",                 "1,240", "2,180", "1,240", "10"],
            ["MCCs",                   "218",   "820",   "218",   "8"],
            ["Transformers",           "148",   "620",   "148",   "6"],
            ["Substations",            "62",    "410",   "62",    "5"],
            ["Instrumentation",        "3,180", "5,420", "3,180", "18"],
            ["Conveyors",              "412",   "1,240", "412",   "12"],
            ["Structural Steel",       "212",   "3,140", "62",    "18"],
            ["Civil Foundations",      "612",   "1,840", "44",    "12"],
            ["Buildings",              "84",    "612",   "22",    "8"],
            ["Process Equipment",      "1,410", "2,940", "1,410", "22"],
        ]},
        aiPrompts: [
            "Which asset class has the highest reuse opportunity across the current portfolio?",
            "Show vendor concentration risk for slurry pumps.",
        ],
    },
    {
        key: "data_sheet_intelligence",
        name: "Engineering Data Sheet Intelligence™",
        section: "Engineering",
        icon: "FileSpreadsheet",
        tagline: "Auto-populate engineering data sheets from historical designs.",
        purpose: "Pick the commodity and equipment — the platform searches the repository, finds similar historical designs, and pre-fills operating conditions, capacity, density, temperature, materials, dimensions and vendor history. Engineers validate only the differences.",
        kpis: [
            { label: "Equipment Classes", value: "10" },
            { label: "Auto-Populated", value: "83%" },
            { label: "Review Fields", value: "17%" },
            { label: "Cycle Time", value: "-85%" },
        ],
        table: { title: "Supported Equipment", columns: ["Equipment", "Historical Refs", "Vendor History", "Auto Fields"], rows: [
            ["Rip Tanks",         "38", "12 vendors", "184"],
            ["Cyclones",          "56", "6 vendors",  "142"],
            ["Thickeners",        "44", "9 vendors",  "168"],
            ["Pumps",             "128","18 vendors", "212"],
            ["Screens",           "62", "8 vendors",  "148"],
            ["Flotation Cells",   "34", "6 vendors",  "138"],
            ["Reactors",          "22", "5 vendors",  "128"],
            ["Pressure Vessels",  "84", "9 vendors",  "168"],
            ["Tanks",             "142","11 vendors", "132"],
            ["Piping Components", "612","18 vendors", "94"],
        ]},
        aiPrompts: [
            "Generate a data sheet for a Rip Tank at 4Mtpa Gold CIP.",
            "Which equipment classes have the strongest historical basis?",
        ],
    },
    {
        key: "plant_baseline_generator",
        name: "Process Plant Baseline Generator™",
        section: "Engineering",
        icon: "Rocket",
        tagline: "Configure commodity, capacity, country, client — get a full plant baseline.",
        purpose: "Given the commodity, capacity, country, client and recovery process, the platform auto-generates the engineering execution strategy, estimate, equipment register, PFDs, procurement list, POP, COP, drawing register, deliverables, review schedule and execution baseline. Supports Gold, Copper, PGM, Coal, Iron Ore, Lithium, Nickel, Cobalt, Green Hydrogen, Water Treatment and Fertiliser.",
        kpis: [
            { label: "Commodities", value: "11" },
            { label: "Baseline Time", value: "< 20 s" },
            { label: "Auto-Composed", value: "88%" },
            { label: "Confidence", value: "High" },
        ],
        table: { title: "Commodities Supported", columns: ["Commodity", "Recovery Process", "Historical Basis", "Confidence"], rows: [
            ["Gold",             "CIP · CIL · Elution",    "38 projects", "High"],
            ["Copper",           "Concentrator · SX-EW",   "22 projects", "High"],
            ["PGM",              "Concentrator · Smelter", "16 projects", "High"],
            ["Coal",             "Beneficiation · DMS",    "18 projects", "High"],
            ["Iron Ore",         "Beneficiation · Pellet", "14 projects", "High"],
            ["Lithium",          "Spodumene · Brine",      "6 projects",  "Medium"],
            ["Nickel",           "Sulfide · HPAL",         "8 projects",  "Medium"],
            ["Cobalt",           "SX-EW",                  "12 projects", "High"],
            ["Green Hydrogen",   "Electrolysis · Storage", "4 projects",  "Medium"],
            ["Water Treatment",  "Desalination · Recovery","10 projects", "High"],
            ["Fertiliser",       "Ammonia · Urea",         "6 projects",  "Medium"],
        ]},
        aiPrompts: [
            "Generate a full plant baseline for a 4Mtpa Gold CIP plant in South Africa for Sibanye-Stillwater.",
            "Compare Gold CIP vs Gold CIL baselines for a new opportunity.",
        ],
    },
    {
        key: "bid_prep_intel",
        name: "Intelligent Bid Preparation™",
        section: "Business Development",
        icon: "Target",
        tagline: "Bid faster with historical intelligence, confidence scoring and estimated engineering savings.",
        purpose: "Recommends historical projects, hours, procurement values, subcontract costs, duration, engineering effort, commercial margins, procurement risks, contractual issues and lessons learned — plus Bid Confidence Score™, Estimated Engineering Saving™, Knowledge Reuse % and Probability of Success™.",
        kpis: [
            { label: "Bid Confidence Score", value: "82" },
            { label: "Est. Engineering Saving", value: "$4.2M" },
            { label: "Knowledge Reuse", value: "62%" },
            { label: "Probability of Success", value: "72%" },
        ],
        table: { title: "Live Bid Radar (drill-down)", columns: ["Opportunity", "Type", "Est. Hrs Saved", "Confidence", "P(Win)"], rows: [
            ["SGSB-OPP-2026-042", "Gold CIP · 4Mtpa",   "2,180", "91", "72%"],
            ["SGSB-OPP-2026-043", "PGM Smelter",        "1,640", "86", "64%"],
            ["SGSB-OPP-2026-044", "Copper Leach",       "1,940", "88", "58%"],
            ["SGSB-OPP-2026-045", "Coal Beneficiation", "1,140", "82", "78%"],
            ["SGSB-OPP-2026-046", "Ammonia FEED",       "820",   "74", "62%"],
        ]},
        aiPrompts: [
            "Recommend a bid strategy for the Sibanye Gold opportunity.",
            "Estimate historical engineering hours for a copper leach expansion.",
        ],
    },
    {
        key: "design_automation",
        name: "Engineering Design Automation™",
        section: "Engineering",
        icon: "Zap",
        tagline: "Auto-generate the schedules engineers used to spend weeks producing.",
        purpose: "Automatically generates equipment schedules, line lists, instrument index, cable schedules, motor schedules, valve schedules, equipment lists, drawing registers, vendor document registers, inspection schedules, review schedules and procurement packages. Engineers review — they never recreate.",
        kpis: [
            { label: "Schedule Types", value: "12" },
            { label: "Auto-Composed", value: "94%" },
            { label: "Time Saved / Package", value: "48 h" },
            { label: "First-Pass Accuracy", value: "96%" },
        ],
        table: { title: "Supported Schedules", columns: ["Schedule", "Auto Fields", "Historical Basis", "Time Saved"], rows: [
            ["Equipment Schedule",       "212 fields", "38 projects", "36 h"],
            ["Line List",                "184 fields", "42 projects", "48 h"],
            ["Instrument Index",         "162 fields", "31 projects", "42 h"],
            ["Cable Schedule",           "148 fields", "28 projects", "40 h"],
            ["Motor Schedule",           "126 fields", "34 projects", "36 h"],
            ["Valve Schedule",           "142 fields", "38 projects", "38 h"],
            ["Equipment List",           "94 fields",  "44 projects", "18 h"],
            ["Drawing Register",         "68 fields",  "62 projects", "16 h"],
            ["Vendor Doc Register",      "82 fields",  "38 projects", "22 h"],
            ["Inspection Schedule",      "112 fields", "26 projects", "28 h"],
            ["Review Schedule",          "74 fields",  "42 projects", "18 h"],
            ["Procurement Package",      "168 fields", "38 projects", "52 h"],
        ]},
        aiPrompts: [
            "Generate an instrument index for a 4Mtpa Gold CIP plant.",
            "Which schedule has the highest reuse leverage this quarter?",
        ],
    },
    {
        key: "lessons_learned_intel",
        name: "Lessons Learned Intelligence™",
        section: "Engineering",
        icon: "BookOpen",
        tagline: "Turn lessons learned into proactive recommendations on similar projects.",
        purpose: "Whenever a similar project is created, the platform surfaces previous mistakes, successful solutions, preferred vendors, and construction / commissioning / procurement / commercial / contract / quality / safety / environmental lessons — with a Knowledge Relevance Score™.",
        kpis: [
            { label: "Lessons Indexed", value: "1,842" },
            { label: "Applied This Year", value: "412" },
            { label: "Avg Relevance Score", value: "88%" },
            { label: "Rework Reduction", value: "-66.8%" },
        ],
        table: { title: "Proactive Recommendations (Gold CIP · new opportunity)", columns: ["Lesson", "Category", "Relevance", "Impact"], rows: [
            ["Slurry line erosion — increase pipe class to SS316L", "Piping",       "94%", "High"],
            ["Confirm HAZOP action closure evidence early",         "Process",      "91%", "High"],
            ["Refractory life below spec on 24 h shifts",           "Process",      "88%", "High"],
            ["Foundation cure schedule slipped in wet season",      "Civil",        "84%", "Medium"],
            ["Motor package delivery risk from single supplier",    "Procurement",  "82%", "High"],
            ["Retention release delayed post-commissioning",        "Commercial",   "78%", "Medium"],
        ]},
        aiPrompts: [
            "Which lessons apply to the new Sibanye Gold opportunity?",
            "Score the top 5 lessons by relevance and impact.",
        ],
    },
    {
        key: "digital_twin",
        name: "Digital Engineering Twin™",
        section: "Engineering",
        icon: "Network",
        tagline: "Every object knows its documents, contracts, procurement, vendors, risks and lessons.",
        purpose: "An enterprise engineering model connecting Engineering, Commercial, Contracts, Procurement, Construction, Commissioning, Quality and Executive Intelligence. Every object carries its drawings, revisions, vendors and lessons — one place, always current.",
        kpis: [
            { label: "Modelled Objects", value: "84,120" },
            { label: "Relationships", value: "412,900" },
            { label: "Update Latency", value: "< 3 s" },
            { label: "Coverage", value: "94%" },
        ],
        table: { title: "Object Relationships (top 10)", columns: ["Object", "Docs", "Contracts", "Vendors", "Lessons"], rows: [
            ["Rip Tank #142 · Kolomela",   "68",  "3", "4", "6"],
            ["Cyclone Battery · Rustenburg","112", "2", "2", "4"],
            ["Reactor Train · Nampula",    "184", "4", "3", "8"],
            ["SAG Mill · Kolomela",        "142", "3", "2", "5"],
            ["Slurry Pump Set · Okavango", "88",  "2", "3", "4"],
            ["MV Switchgear · Rustenburg", "62",  "1", "1", "2"],
            ["Compressor Package · Saldanha","94", "2", "1", "3"],
            ["Motor Package · Kolomela",   "112", "2", "2", "4"],
            ["Refinery Cranes · Lubumbashi","44",  "1", "1", "1"],
            ["Foundation Pad · Nampula",   "68",  "1", "1", "3"],
        ]},
        aiPrompts: [
            "Show me everything linked to Rip Tank #142 on Kolomela.",
            "Which objects carry the highest procurement risk this quarter?",
        ],
    },
    {
        key: "commodity_libraries",
        name: "Commodity Knowledge Libraries™",
        section: "Knowledge",
        icon: "Boxes",
        tagline: "Dedicated engineering libraries for the commodities SGS Bateman delivers.",
        purpose: "Each commodity library contains process knowledge, standard specifications, preferred templates, vendor knowledge, equipment, engineering standards and historical projects.",
        kpis: [
            { label: "Libraries", value: "10" },
            { label: "Total Assets", value: "84,120" },
            { label: "Templates", value: "612" },
            { label: "Preferred Vendors", value: "148" },
        ],
        table: { title: "Commodity Libraries", columns: ["Commodity", "Projects", "Templates", "Vendors", "Reuse %"], rows: [
            ["Gold",           "48", "142", "22", "62%"],
            ["Copper",         "26", "98",  "18", "58%"],
            ["PGM",            "18", "82",  "14", "48%"],
            ["Coal",           "22", "74",  "16", "51%"],
            ["Iron Ore",       "16", "68",  "14", "54%"],
            ["Lithium",        "8",  "34",  "8",  "38%"],
            ["Nickel",         "10", "42",  "10", "41%"],
            ["Cobalt",         "14", "48",  "12", "47%"],
            ["Green Hydrogen", "6",  "28",  "8",  "34%"],
            ["Water",          "12", "56",  "10", "44%"],
        ]},
        aiPrompts: [
            "Which commodity library has the strongest reuse leverage right now?",
            "Recommend which library to invest in this quarter for maximum ROI.",
        ],
    },
    {
        key: "knowledge_marketplace",
        name: "Engineering Knowledge Marketplace™",
        section: "Executive",
        icon: "Search",
        tagline: "One enterprise search across every reusable engineering asset.",
        purpose: "Search drawings, calculations, standards, vendor packages, specifications, PFDs, P&IDs, equipment, templates and lessons learned — with similarity ranking, recommended reuse, estimated engineering savings and confidence score.",
        kpis: [
            { label: "Assets Indexed", value: "2.41M" },
            { label: "Avg Latency", value: "6.2 s" },
            { label: "Precision", value: "97.8%" },
            { label: "Est. Savings / Query", value: "$18k" },
        ],
        table: { title: "Recent Queries", columns: ["Query", "Hits", "Top Similarity", "Est. Savings"], rows: [
            ["Rip Tank P&ID · Gold CIP",         "42", "94%", "$14k"],
            ["Slurry pump 100 m³/h duty",        "128","91%", "$8k"],
            ["Refractory life > 24 months",      "22", "88%", "$22k"],
            ["Foundation loading — steel structure","68","84%","$6k"],
            ["Retention clause template · EPCM", "24", "82%", "$3k"],
        ]},
        aiPrompts: [
            "Search for reusable Gold CIP P&IDs.",
            "Which query patterns are trending this month?",
        ],
    },
    {
        key: "ip_protection_plus",
        name: "Intellectual Property Protection™",
        section: "Executive",
        icon: "ShieldCheck",
        tagline: "Ownership, classification, permissions, audit trail and lifecycle.",
        purpose: "Extends Intellectual Capital Protection with knowledge ownership, engineering ownership, reuse permissions, knowledge classification, sensitive document controls, confidentiality, usage history, download history and full audit trail.",
        kpis: [
            { label: "Sensitivity Levels", value: "5" },
            { label: "Audit Events (30d)", value: "62,180" },
            { label: "Access Denials", value: "24" },
            { label: "Coverage", value: "100%" },
        ],
        table: { title: "Sensitivity Register", columns: ["Level", "Assets", "Access Rule", "Audit"], rows: [
            ["Public",                 "412,180", "All authenticated users",          "Standard"],
            ["Internal",               "1.42M",   "SGS Bateman staff",                "Standard"],
            ["Client-Restricted",      "418,200", "Named client + SGS Bateman only",  "Enhanced"],
            ["Commercially Sensitive", "148,400", "Commercial + Executive only",      "Enhanced"],
            ["Highly Confidential",    "34,120",  "Executive + Board only",           "Full audit trail"],
        ]},
        aiPrompts: [
            "Which sensitive assets were accessed by whom this week?",
            "Recommend permission tightening for Highly Confidential class.",
        ],
    },
];

export const EDI_MODULES_BY_KEY = Object.fromEntries(
    EDI_MODULES.map((m) => [m.key, m]),
);

export function edi_by_section() {
    const grouped = {};
    for (const m of EDI_MODULES) {
        (grouped[m.section] ||= []).push(m);
    }
    return grouped;
}
